import { useFlyOutProductsBasedOnSelectedCloud } from '../../common/utils/hooks/useFlyOutProductsBasedOnSelectedCloud'
import { FlyoutFunction } from '../../modules/productsListing/types'
import { setCloudMarketplaceProductListing } from '../../modules/productListingFilter/action'
import { useEffect, useMemo, ReactNode } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from '../../store'
import {
  LoadingTypes,
  isLoading,
  useCurrentCloudProvider,
  getFlyOutFunctionTypeBasedOnUrl,
  setSelectedAccount,
  usePartnerAccountDataActions,
  PartnerType,
  type AccountDataSelection,
} from '@labrav/common'
import { CloudType as CloudTypeEnum } from '../../types/external'
import type { CloudMarketplace } from '../../modules/productsListing/types'
import { isEmpty } from 'lodash'
import { makeStyles } from 'tss-react/mui'
import { clearResaleAuthorizations } from '../../admin/modules/resaleAuthorizations/actions'
import { clearPrivateOffers } from '../../modules/privateOffer/action'
import { useFlagValue } from '@labrav/flags'
import { loadAvailableAccounts } from '../../components/FlyoutSettings/accountSelectionActions'
import {
  getProducts,
  getProductsV2,
} from '../../modules/productsListing/action'
import { getFlyoutListingData } from '../../modules/flyOutSyncs/action'

export const useStyles = makeStyles<{ isV2?: boolean }>()(
  (theme, { isV2 }) => ({
    root: {
      backgroundColor: isV2
        ? theme.palette.textBackground.light
        : theme.palette.opaque.light,
    },
    content: {
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      backgroundColor: isV2
        ? theme.palette.textBackground.light
        : theme.palette.opaque.light,
    },
    appBody: {
      width: '100%',
    },
  })
)

const PartnerProductsWrapper: React.FC<{
  children:
    | ReactNode
    | React.FC<{
        externalBaseURL?: string
        search?: string
        crmOpportunityId?: string
      }>
  isV2?: boolean
}> = ({ children, isV2 = false }) => {
  const { search, pathname } = useLocation()
  const navigate = useNavigate()
  const { classes } = useStyles({ isV2 })
  const dispatch = useDispatch()
  const { getAccount } = usePartnerAccountDataActions()
  const cloudMarketplace = useCurrentCloudProvider()
  const {
    secureKey,
    partnerId,
    cloud: routeCloud,
    accountId: routeAccountId,
  } = useParams<{
    secureKey: string
    partnerId: string
    cloud?: string
    accountId?: string
  }>()

  const hasAccountInRoute = Boolean(routeCloud && routeAccountId)

  const loading = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.GENERAL)
  )
  const loadingProducts = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.FETCH_PRODUCTS)
  )
  const createPrivateOfferModalOpen = useSelector<boolean>(
    state => state.productListingFilters?.createPrivateOfferModalOpen ?? false
  )

  const currentFlyoutFunction = getFlyOutFunctionTypeBasedOnUrl({
    pathname: pathname,
  })
  const { products: productRows } = useFlyOutProductsBasedOnSelectedCloud()
  const { value: auth0Enabled, loading: isFlagsLoading } = useFlagValue('auth0')

  const { partnerData } = useSelector(state => state.AccountData.user)
  const availableAccounts = useSelector(state => {
    const accounts = state.accountSelection?.availableAccounts
    return Array.isArray(accounts) ? accounts : []
  })
  const flyoutAccounts = availableAccounts

  const crmOpportunityId = new URLSearchParams(search)
    .get('hs_object_id')
    ?.toLowerCase()

  const externalBaseURL = useMemo(() => {
    const base = `/external-apps/hubspot/v1/${secureKey}/partners/${partnerId}`
    if (hasAccountInRoute && routeCloud && routeAccountId) {
      return `${base}/cloud/${routeCloud}/accounts/${routeAccountId}/product-listing`
    }
    return `${base}/product-listing`
  }, [secureKey, partnerId, hasAccountInRoute, routeCloud, routeAccountId])

  // When URL has no accountId (legacy route), load accounts and redirect to first flyout account.
  // Only redirect when on the base product-listing page - NOT when on sub-routes like
  // private-offers/details, private-offers/edit, resale-authorizations, etc.
  const isBaseProductListingRoute = pathname.endsWith('/product-listing')
  useEffect(() => {
    if (
      !partnerId ||
      !secureKey ||
      hasAccountInRoute ||
      !isBaseProductListingRoute ||
      isFlagsLoading ||
      isEmpty(flyoutAccounts)
    ) {
      return
    }
    const first = flyoutAccounts[0] as AccountDataSelection | undefined
    if (first?.id && first?.cloud) {
      const cloudPath = String(first.cloud).toLowerCase()
      const searchPart = search
        ? search.startsWith('?')
          ? search
          : `?${search}`
        : ''
      navigate(
        `/external-apps/hubspot/v1/${secureKey}/partners/${partnerId}/cloud/${cloudPath}/accounts/${first.id}/product-listing${searchPart}`,
        { replace: true }
      )
    }
  }, [
    partnerId,
    secureKey,
    hasAccountInRoute,
    isBaseProductListingRoute,
    isFlagsLoading,
    flyoutAccounts,
    navigate,
    search,
  ])

  const fetchPageData = async () => {
    if (!partnerId || !secureKey) return

    if (isEmpty(partnerData) || partnerData?.partnerId !== partnerId) {
      await dispatch(getAccount(partnerId, PartnerType.User) as never)
    }
    if (availableAccounts.length === 0) {
      await dispatch(loadAvailableAccounts(partnerId))
    }

    if (hasAccountInRoute && routeAccountId && routeCloud) {
      const cloudUpper = routeCloud.toUpperCase() as CloudMarketplace
      dispatch(setSelectedAccount(routeAccountId, 'flyout', cloudUpper))
      dispatch(setCloudMarketplaceProductListing(cloudUpper))
      cloudUpper === CloudTypeEnum.AWS &&
        dispatch(
          getFlyoutListingData({ partnerId: routeAccountId, metadata: true })
        )
      // Only fetch products and clear offers when on the product listing page itself,
      // not when on sub-routes (e.g. create/edit private offer, resale auth, details).
      if (isBaseProductListingRoute) {
        currentFlyoutFunction === FlyoutFunction.PRIVATE_OFFERS &&
          dispatch(clearPrivateOffers())
        currentFlyoutFunction === FlyoutFunction.RESALE_AUTHORIZATIONS &&
          dispatch(clearResaleAuthorizations())
        await dispatch(getProducts(routeAccountId, cloudUpper))
        await dispatch(getProductsV2(routeAccountId, cloudUpper))
      }
    } else if (!hasAccountInRoute) {
      // Legacy URL: only load partner and accounts; redirect will then load products for selected account
      if (isBaseProductListingRoute) {
        currentFlyoutFunction === FlyoutFunction.PRIVATE_OFFERS &&
          dispatch(clearPrivateOffers())
        currentFlyoutFunction === FlyoutFunction.RESALE_AUTHORIZATIONS &&
          dispatch(clearResaleAuthorizations())
      }
    }
  }

  useEffect(() => {
    if (!isFlagsLoading) {
      fetchPageData()
    }
  }, [
    secureKey,
    partnerId,
    routeAccountId,
    routeCloud,
    hasAccountInRoute,
    isFlagsLoading,
    auth0Enabled,
  ])

  const showLoading =
    !createPrivateOfferModalOpen &&
    (isEmpty(partnerData) ||
      isFlagsLoading ||
      (hasAccountInRoute && loading && isEmpty(productRows)) ||
      (!hasAccountInRoute && isEmpty(flyoutAccounts)) ||
      loadingProducts)

  if (showLoading) {
    return <div data-testid="loading"></div>
  }
  return (
    <div className={classes.root} data-testid="partner-products-wrapper">
      <div className={classes.content}>
        {typeof children === 'function'
          ? children({ externalBaseURL, search, crmOpportunityId })
          : children}
      </div>
    </div>
  )
}

export default PartnerProductsWrapper
